import { Colors } from 'src/shared/components/styles';
import { MoreVert, Download, Visibility } from '@mui/icons-material';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import { DocumentResponse } from 'src/shared/services/api/response/documentResponse';
import { GetAdminPermission } from 'src/shared/helpers/permissionHelper';
import { useTranslation } from 'react-i18next';

type DocumentItemProps = {
  item: DocumentResponse;
  companyId: number;
  handleClickShowActionMenu: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  onDownloadFileClicked: (item: DocumentResponse) => void;
  onPreviewFileClicked: (item: DocumentResponse) => void;
};

const PREVIEWABLE_MIMES = new Set([
  'image/bmp',
  'text/csv',
  'application/vnd.oasis.opendocument.text',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/gif',
  'text/html',
  'image/jpeg',
  'application/pdf',
  'image/png',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/tiff',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

function canPreviewByMime(mime?: string): boolean {
  return mime ? PREVIEWABLE_MIMES.has(mime.toLowerCase()) : false;
}

export const DocumentItemComponent = ({
  item,
  companyId,
  handleClickShowActionMenu,
  onDownloadFileClicked,
  onPreviewFileClicked,
}: DocumentItemProps) => {
  const { t } = useTranslation();

  const isAdmin = GetAdminPermission(companyId, 'DeleteDocument');
  const previewable = canPreviewByMime(item.mimeType);
  const normalizedMime = item.mimeType ?? 'unknown';
  return (
    <ListItem
      divider
      disablePadding
      key={item.id}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Preview */}
          <Tooltip
            title={
              previewable
                ? t('Preview')
                : `${t('Cant preview')} "${normalizedMime}"`
            }
          >
            <span>
              <IconButton
                edge='end'
                aria-label='preview'
                disabled={!previewable}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewFileClicked(item);
                }}
              >
                <Visibility />
              </IconButton>
            </span>
          </Tooltip>

          {/* Download */}
          <Tooltip title={t('Download')}>
            <IconButton
              edge='end'
              aria-label='download'
              onClick={(e) => {
                e.stopPropagation();
                onDownloadFileClicked(item);
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>

          {/* Admin menu (three dots) */}
          {isAdmin && (
            <Tooltip title={t('More')}>
              <IconButton
                edge='end'
                aria-label={t('more')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickShowActionMenu(e);
                }}
              >
                <MoreVert sx={{ color: Colors.ORANGE }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
    >
      <ListItemButton
        onClick={() => onDownloadFileClicked(item)} // keep row click as download (or change if you prefer)
      >
        <ListItemText
          primaryTypographyProps={{ fontSize: 14 }}
          primary={item.name}
          sx={{ paddingRight: 16 }}
        />
      </ListItemButton>
    </ListItem>
  );
};
